<template>
  <div class="mpc-section" v-if="hasData">
    <div class="mpc-header">
      <div class="mpc-header-icon">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="12" y1="1" x2="12" y2="23"/>
          <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>
        </svg>
      </div>
      <div>
        <h3 class="mpc-title">{{ $t('marketPriceChart.title') }}</h3>
        <p class="mpc-count">{{ $t('marketPriceChart.count', { count: toFa(count) }) }}</p>
      </div>
    </div>

    <div class="mpc-range-bar">
      <div class="range-labels">
        <span class="range-max">{{ toFa(formatNumber(maxPrice)) }} <small>{{ $t('marketPriceChart.currency') }}</small></span>
        <span class="range-min">{{ toFa(formatNumber(minPrice)) }} <small>{{ $t('marketPriceChart.currency') }}</small></span>
      </div>
      <div class="range-track">
        <div class="range-fill" :style="{ width: fillPercent + '%' }"></div>
        <div class="range-pointer" :style="{ left: pointerPercent + '%' }">
          <div class="pointer-dot"></div>
          <div class="pointer-label">{{ toFa(formatNumber(currentPrice)) }} <small>{{ $t('marketPriceChart.currency') }}</small></div>
        </div>
      </div>
      <div class="range-status">
        <div class="status-item status-high" :class="{ 'is-active': position === 'high' }">{{ $t('marketPriceChart.status_premium') }}</div>
        <div class="status-item status-mid" :class="{ 'is-active': position === 'mid' }">{{ $t('marketPriceChart.status_fair') }}</div>
        <div class="status-item status-low">{{ $t('marketPriceChart.status_good') }}</div>
      </div>
    </div>

    <div class="mpc-chart-wrap">
      <canvas ref="chartCanvas"></canvas>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import { Chart, registerables } from 'chart.js'

const { t } = useI18n()
const localePath = useLocalePath()

Chart.register(...registerables)

const props = defineProps({
  categoryValue: { type: [String, Number], default: null },
  currentPrice: { type: Number, default: 0 }
})

const chartCanvas = ref(null)
const prices = ref([])
const labels = ref([])
const minPrice = ref(0)
const maxPrice = ref(0)
const count = ref(0)
const position = ref('mid')
let chartInstance = null

const hasData = computed(() => prices.value.length > 0)

const fillPercent = computed(() => {
  if (maxPrice.value === minPrice.value) return 50
  return ((props.currentPrice - minPrice.value) / (maxPrice.value - minPrice.value)) * 100
})

const pointerPercent = computed(() => {
  if (maxPrice.value === minPrice.value) return 50
  let p = ((props.currentPrice - minPrice.value) / (maxPrice.value - minPrice.value)) * 100
  return Math.max(5, Math.min(95, p))
})

const formatNumber = (num) => {
  return new Intl.NumberFormat('fa-IR').format(num)
}

const toFa = (val) => {
  if (val === null || val === undefined) return ''
  return String(val).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[d])
}

const buildChart = () => {
  if (!chartCanvas.value || prices.value.length === 0) return
  if (chartInstance) { chartInstance.destroy(); chartInstance = null }

  const barColors = prices.value.map(p =>
    p === props.currentPrice ? '#03a054' : 'rgba(200, 200, 200, 0.5)'
  )
  const borderColors = prices.value.map(p =>
    p === props.currentPrice ? '#03a054' : 'rgba(200, 200, 200, 0.3)'
  )

  chartInstance = new Chart(chartCanvas.value, {
    type: 'bar',
    data: {
      labels: labels.value,
      datasets: [{
        label: t('marketPriceChart.chart_label'),
        data: prices.value,
        backgroundColor: barColors,
        borderColor: borderColors,
        borderWidth: p => prices.value[p.dataIndex] === props.currentPrice ? 2 : 1,
        borderRadius: 4,
        barPercentage: 0.6
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      onClick: (_, elements) => {
        if (elements.length > 0) {
          const idx = elements[0].index
          const invoiceNum=labels.value[idx].split('-')[0]
          navigateTo(localePath(`/details/${invoiceNum}`))
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: { color: 'rgba(226, 214, 189, 0.3)' },
          ticks: {
            font: { family: 'IRANYekan, Vazirmatn, sans-serif' },
            callback: v => v.toLocaleString('fa-IR')
          }
        },
        x: {
          grid: { display: false },
          ticks: {
            maxRotation: 45,
            font: { size: 10, family: 'IRANYekan, Vazirmatn, sans-serif' },
            color: '#666'
          }
        }
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: 'rgba(51, 51, 51, 0.95)',
          titleFont: { family: 'IRANYekan, Vazirmatn, sans-serif' },
          bodyFont: { family: 'IRANYekan, Vazirmatn, sans-serif' },
          callbacks: {
            title: items => t('marketPriceChart.tooltip_code', { code: toFa(items[0].label) }),
            label: ctx => t('marketPriceChart.tooltip_price', { price: toFa(formatNumber(ctx.raw)) })
          }
        }
      }
    }
  })
}

const fetchData = async () => {
  if (!props.categoryValue) return
  try {
    const data = await $fetch(`/market/api/market/price-range/${props.categoryValue}`)
    if (!data || !Array.isArray(data) || data.length === 0) return

    const valid = data.filter(d => d.price && d.invoiceNum)
    prices.value = valid.map(d => Number(d.price))
    labels.value = valid.map(d => String(d.invoiceNum)+'-'+ String(d.title.replace('فروش','') || ''))
    minPrice.value = Math.min(...prices.value)
    maxPrice.value = Math.max(...prices.value)
    count.value = valid.length

    if (props.currentPrice <= minPrice.value) position.value = 'low'
    else if (props.currentPrice >= maxPrice.value) position.value = 'high'
    else position.value = 'mid'

    await nextTick()
    buildChart()
  } catch (e) {
    console.error('Market price chart error:', e)
  }
}

watch(() => props.categoryValue, fetchData)

onMounted(fetchData)
</script>

<style lang="scss" scoped>
.mpc-section {
  margin: 20px 16px 0;
  background: #fff;
  border-radius: $r-lg;
  border: 1px solid rgba(226, 214, 189, 0.55);
  box-shadow: $shadow-warm-sm;
  overflow: hidden;
  padding: 20px 20px 16px;
  font-family: 'IRANYekan', 'Vazirmatn', sans-serif;
  direction: rtl;
  color: $ink;
}

.mpc-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
}

.mpc-header-icon {
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  border-radius: $r-sm;
  background: rgba(3, 160, 84, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  color: $leaf;

  svg { width: 18px; height: 18px; }
}

.mpc-title {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 800;
  color: $ink;
}

.mpc-count {
  margin: 2px 0 0;
  font-size: 0.72rem;
  color: $ink-soft;
}

// ============================================================
//  RANGE BAR
// ============================================================
.mpc-range-bar {
  margin-bottom: 20px;
  padding: 14px 16px;
  background: linear-gradient(135deg, rgba(3, 160, 84, 0.04), rgba(108, 177, 122, 0.02));
  border-radius: $r-md;
  border: 1px solid rgba(3, 160, 84, 0.08);
}

.range-labels {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;

  span {
    font-size: 0.75rem;
    font-weight: 700;
    color: $ink;

    small {
      font-weight: 500;
      font-size: 0.65rem;
      color: $ink-soft;
      margin-right: 2px;
    }
  }
}

.range-track {
  position: relative;
  height: 8px;
  background: rgba(226, 214, 189, 0.5);
  border-radius: 999px;
  margin-bottom: 12px;
  direction: ltr;
}

.range-fill {
  position: absolute;
  height: 100%;
  background: linear-gradient(90deg, rgba(3, 160, 84, 0.3), $leaf);
  border-radius: 999px;
  transition: width 0.6s cubic-bezier(0.16, 1, 0.3, 1);
}

.range-pointer {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
  transition: left 0.6s cubic-bezier(0.16, 1, 0.3, 1);
}

.pointer-dot {
  width: 16px;
  height: 16px;
  background: $leaf;
  border: 3px solid #fff;
  border-radius: 50%;
  box-shadow: 0 2px 8px rgba(3, 160, 84, 0.4);
  margin: 0 auto 4px;
}

.pointer-label {
  white-space: nowrap;
  font-size: 0.7rem;
  font-weight: 700;
  color: $leaf;
  text-align: center;
  margin-top: 10px;

  small {
    font-weight: 500;
    font-size: 0.6rem;
    color: $ink-soft;
  }
}

.range-status {
  display: flex;
  justify-content: space-between;
  margin-top: 25px;
}

.status-item {
  font-size: 0.68rem;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: $r-pill;
  color: $ink-soft;
  background: #f8f9fa;
  transition: all 0.3s;

  &.is-active {
    background: $leaf;
    color: #fff;
    box-shadow: 0 3px 10px rgba(3, 160, 84, 0.25);
  }

  &.status-low.is-active { background: $leaf; }
  &.status-high.is-active { background: $sun-deep; }
}

// ============================================================
//  CHART
// ============================================================
.mpc-chart-wrap {
  position: relative;
  height: 200px;
  background: #fff;

  canvas {
    width: 100% !important;
    height: 100% !important;
    background: #fff;
  }
}

// ============================================================
//  RESPONSIVE
// ============================================================
@media (min-width: 768px) {
  .mpc-chart-wrap {
    height: 240px;
  }
}

@media (min-width: 992px) {
  .mpc-section {
    margin: 20px 24px 0;
    padding: 24px 24px 20px;
  }

  .mpc-chart-wrap {
    height: 260px;
  }
}
</style>
